/** People Landing — editor de bloques. Hasta el 21/09/2026 vivía en BlockEditorDialog.tsx (1.017 líneas). */
import { AlignCenter, AlignLeft, AlignRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type HeroStyle, type LandingAlign } from '../../../../../shared/types/people-landing';

export function AlignControl({ value, onChange }: { value: LandingAlign; onChange: (a: LandingAlign) => void }) {
  const opts: { v: LandingAlign; Icon: React.ElementType }[] = [
    { v: 'left', Icon: AlignLeft },
    { v: 'center', Icon: AlignCenter },
    { v: 'right', Icon: AlignRight },
  ];
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
      {opts.map(({ v, Icon }) => (
        <button
          key={v}
          type="button"
          aria-label={v}
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'h-8 w-9 rounded-md flex items-center justify-center transition-colors',
            value === v ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

/** Selector visual de preset del hero: 4 miniaturas en CSS, no un <select> de texto. */
export function HeroStylePicker({
  value,
  onChange,
  labels,
}: {
  value: HeroStyle;
  onChange: (s: HeroStyle) => void;
  labels: Record<HeroStyle, string>;
}) {
  const thumbs: Record<HeroStyle, React.ReactNode> = {
    classic: <div className="absolute inset-0 bg-linear-to-br from-brand-purple-pill via-brand-purple to-brand-green"><div className="absolute inset-0 bg-black/45" /></div>,
    aurora: (
      <div className="absolute inset-0 bg-white">
        <div className="absolute -left-4 -top-6 h-16 w-16 rounded-full bg-brand-purple blur-md opacity-80" />
        <div className="absolute -right-3 -top-4 h-14 w-14 rounded-full bg-brand-green blur-md opacity-70" />
        <div className="absolute left-8 -bottom-8 h-16 w-16 rounded-full bg-brand-blue blur-md opacity-70" />
      </div>
    ),
    spotlight: (
      <div className="absolute inset-0 bg-brand-dark">
        <div className="absolute left-3 top-1 h-16 w-16 rounded-full bg-brand-purple blur-lg opacity-60" />
        <div className="absolute left-5 top-0 h-full w-px bg-brand-green/70" />
        <div className="absolute left-14 top-0 h-full w-px bg-brand-blue/70" />
      </div>
    ),
    grid: (
      <div
        className="absolute inset-0 bg-brand-offwhite"
        style={{ backgroundImage: 'radial-gradient(rgba(20,20,20,.25) 1px, transparent 1px)', backgroundSize: '8px 8px' }}
      >
        <div className="absolute -right-3 -top-3 h-12 w-12 rounded-full bg-brand-green blur-md opacity-60" />
      </div>
    ),
  };
  return (
    <div className="grid grid-cols-4 gap-2">
      {(Object.keys(thumbs) as HeroStyle[]).map((st) => (
        <button
          key={st}
          type="button"
          aria-pressed={value === st}
          onClick={() => onChange(st)}
          className={cn(
            'group rounded-lg border-2 p-1 text-left transition-colors',
            value === st ? 'border-brand-green' : 'border-border hover:border-foreground/30'
          )}
        >
          <div className="relative h-14 w-full overflow-hidden rounded-md">{thumbs[st]}</div>
          <div className="mt-1 px-0.5 text-[11px] font-medium text-muted-foreground group-aria-pressed:text-foreground">
            {labels[st]}
          </div>
        </button>
      ))}
    </div>
  );
}
